<template>
  <div class="story-plays-page">
    <div class="page-header">
      <h2>剧情播放管理</h2>
      <div class="page-actions">
        <el-input
          v-model="searchQuery"
          placeholder="搜索标题"
          clearable
          style="width: 220px; margin-right: 10px;"
          @keyup.enter.native="handleSearch"
        />
        <el-button type="primary" @click="handleCreate">新建剧情集</el-button>
        <el-button type="warning" icon="el-icon-upload" @click="handleImport">导入</el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="storyPlays"
      border
      style="width: 100%"
    >
      <el-table-column prop="id" label="ID" width="80" align="center" />
      <el-table-column prop="title" label="标题" min-width="180" show-overflow-tooltip />
      <el-table-column label="场景构成" width="140" align="center">
        <template slot-scope="scope">
          <div class="composition-info">
            <span v-if="scope.row.image_count" title="图片场景" class="comp-item img-comp">
              <i class="el-icon-picture" /> {{ scope.row.image_count }}
            </span>
            <span v-if="scope.row.video_count" title="视频场景" class="comp-item vid-comp">
              <i class="el-icon-video-camera" /> {{ scope.row.video_count }}
            </span>
            <span v-if="!scope.row.image_count && !scope.row.video_count" style="color: #999">—</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="配音状态" width="120" align="center">
        <template slot-scope="scope">
          <el-tag
            v-if="scope.row.image_count > 0"
            :type="scope.row.missing_dubbing ? 'warning' : 'success'"
            size="mini"
          >
            {{ scope.row.missing_dubbing ? '配音待补' : '已配置' }}
          </el-tag>
          <span v-else style="color: #999">—</span>
        </template>
      </el-table-column>
      <el-table-column label="场景最后更新" width="160" align="center">
        <template slot-scope="scope">
          {{ scope.row.scene_last_updated || '—' }}
        </template>
      </el-table-column>
      <el-table-column prop="description" label="描述" min-width="180" show-overflow-tooltip />
      <el-table-column label="封面" width="100" align="center">
        <template slot-scope="scope">
          <img
            v-if="scope.row.cover_image"
            :src="resolveMediaUrl(scope.row.cover_image)"
            style="width: 60px; height: 40px; object-fit: cover; border-radius: 4px; cursor: pointer;"
            @click="$router.push(`/story-plays/${scope.row.id}/edit`)"
          >
          <span v-else style="color: #c0c4cc;">—</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="400" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="$router.push(`/story-plays/${scope.row.id}/edit`)">场景</el-button>
          <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button type="text" @click="handlePlay(scope.row)">播放</el-button>
          <el-button type="text" @click="handleExport(scope.row)">导出</el-button>
          <el-button type="text" :loading="bundlePreparingId === scope.row.id" @click="handleBundleDownload(scope.row)">下载包</el-button>
          <el-button type="text" style="color: #F56C6C" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="perPage"
        :total="total"
        @current-change="handlePageChange"
      />
    </div>

    <!-- Create / Edit Dialog -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="560px"
    >
      <el-form label-width="80px">
        <el-form-item label="标题">
          <el-input v-model="form.title" placeholder="请输入标题" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="4" placeholder="请输入描述" />
        </el-form-item>
        <el-form-item label="封面图">
          <div class="cover-uploader">
            <el-upload
              v-if="!form.cover_image"
              :show-file-list="false"
              :http-request="handleCoverUpload"
              action="#"
              accept="image/*"
              class="cover-upload-inner"
            >
              <i class="el-icon-plus cover-uploader-icon" />
            </el-upload>
            <img
              v-else
              :src="resolveMediaUrl(form.cover_image)"
              class="cover-preview"
              @click="form.cover_image = ''"
            >
          </div>
          <el-button
            v-if="form.cover_image"
            size="mini"
            type="text"
            style="margin-top: 6px;"
            @click="form.cover_image = ''"
          >
            移除封面
          </el-button>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">保存</el-button>
      </div>
    </el-dialog>

    <!-- Import Dialog -->
    <el-dialog
      title="导入剧情集"
      :visible.sync="importDialogVisible"
      width="640px"
    >
      <el-upload
        drag
        :show-file-list="false"
        :http-request="handleImportUpload"
        action="#"
        accept=".json"
        style="width: 100%; margin-bottom: 20px;"
      >
        <i class="el-icon-upload" />
        <div class="el-upload__text">
          将 JSON 文件拖到此处，或 <em>点击上传</em>
        </div>
      </el-upload>

      <el-collapse>
        <el-collapse-item title="查看 JSON 格式说明">
          <div class="json-help">
            <p><strong>基础字段：</strong></p>
            <ul>
              <li><code>version</code>: 必须为 "1.0"</li>
              <li><code>title</code>: 剧情集标题 (必填)</li>
              <li><code>description</code>: 剧情集描述 (可选)</li>
              <li><code>cover_image</code>: 封面图的 Base64 编码或 URL (可选)</li>
              <li><code>scenes</code>: 场景数组 (包含多个场景对象)</li>
            </ul>
            <p><strong>scenes 场景字段：</strong></p>
            <ul>
              <li><code>sort_order</code>: 场景排序，用于控制播放顺序和跳转 (必填)</li>
              <li><code>name</code>: 场景名称 (可选)</li>
              <li><code>bg_type</code>: 背景类型 ("image", "video", "minigame", "third_party_game")</li>
              <li><code>bg_image</code>: 背景媒体的 Base64 编码或 URL</li>
              <li><code>bg_prompt</code>: 背景提示词 (字符串，必填。需写清楚风格，如：赛博朋克动漫)</li>
              <li>
                <code>lines</code>: 台词数组，每项可为纯文本，或对象，对象可包含以下字段：
                <ul>
                  <li><code>text</code>: 台词文本 (字符串，必填)</li>
                  <li><code>speaker_name</code>: 说话者名称 (字符串，可选)</li>
                  <li><code>speaker_asset_id</code>: 说话者资源ID (整数或字符串，可选)</li>
                  <li><code>emotion</code>: 情感 (字符串，可选)。可选值：<code>happy</code>(开心), <code>sad</code>(悲伤), <code>angry</code>(愤怒), <code>fear</code>(恐惧), <code>surprise</code>(惊喜), <code>disgust</code>(厌恶), <code>serious</code>(严肃), <code>gentle</code>(温柔), <code>customer_service</code>(客服), <code>professional</code>(专业), <code>storytelling</code>(故事), <code>radio</code>(电台), <code>sajiao</code>(撒娇), <code>aojiao</code>(傲娇), <code>kuxiang</code>(哭腔), <code>comfort</code>(安慰), <code>sorry</code>(抱歉), <code>pleasure</code>(愉悦)</li>
                  <li><code>emotion_scale</code>: 情感强度 (浮点数，可选)，范围 0.1 到 2.0，默认 1.0</li>
                  <li><code>speech_rate</code>: 语速 (浮点数，可选)，范围 0.5 到 2.0，默认 1.0</li>
                  <li><code>audio_url</code>: 音频资源链接或 Base64 (字符串，可选)</li>
                </ul>
              </li>
              <li><code>options</code>: 选项数组，如 <code>[{"text": "同意", "next_scene_sort": 2}]</code></li>
              <li><code>next_scene_sort</code>: 默认跳转的下一个场景的 <code>sort_order</code></li>
            </ul>
            <p><strong>示例：</strong></p>
            <pre class="json-example"><code>{
  "version": "1.0",
  "title": "测试剧情",
  "scenes": [
    {
      "sort_order": 1,
      "name": "开场",
      "bg_type": "image",
      "lines": ["欢迎来到第一幕"],
      "next_scene_sort": 2
    }
  ]
}</code></pre>
          </div>
        </el-collapse-item>
      </el-collapse>

      <div slot="footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getStoryPlays,
  createStoryPlay,
  updateStoryPlay,
  deleteStoryPlay,
  exportStoryPlay,
  prepareStoryPlayBundleDownload,
  importStoryPlay,
  uploadStoryImage
} from "@/net/api";
import { resolveMediaUrl } from "@/utils/media";

export default {
  name: "StoryPlays",
  data() {
    return {
      storyPlays: [],
      loading: false,
      page: 1,
      perPage: 20,
      total: 0,
      searchQuery: "",
      dialogVisible: false,
      dialogTitle: "新建剧情集",
      saving: false,
      form: { id: null, title: "", description: "", cover_image: "" },
      importDialogVisible: false,
      bundlePreparingId: null
    };
  },
  created() {
    this.loadStoryPlays();
  },
  methods: {
    resolveMediaUrl,
    async loadStoryPlays() {
      this.loading = true;
      try {
        const { data } = await getStoryPlays({
          page: this.page,
          per_page: this.perPage,
          search: this.searchQuery
        });
        this.storyPlays = data.items || data.list || data || [];
        this.total = data.total || this.storyPlays.length;
      } catch (e) {
        this.$message.error("加载剧情集失败");
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.page = 1;
      this.loadStoryPlays();
    },
    handlePageChange(page) {
      this.page = page;
      this.loadStoryPlays();
    },
    handleCreate() {
      this.form = { id: null, title: "", description: "", cover_image: "" };
      this.dialogTitle = "新建剧情集";
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.form = { id: row.id, title: row.title || "", description: row.description || "", cover_image: row.cover_image || "" };
      this.dialogTitle = "编辑剧情集";
      this.dialogVisible = true;
    },
    async handleSave() {
      if (!this.form.title.trim()) {
        this.$message.warning("标题不能为空");
        return;
      }
      this.saving = true;
      try {
        if (this.form.id) {
          await updateStoryPlay(this.form.id, this.form);
        } else {
          await createStoryPlay(this.form);
        }
        this.dialogVisible = false;
        this.$message.success("保存成功");
        this.loadStoryPlays();
      } catch (e) {
        this.$message.error("保存失败");
      } finally {
        this.saving = false;
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm("确定删除该剧情集吗？", "提示", { type: "warning" });
        await deleteStoryPlay(row.id);
        this.$message.success("删除成功");
        this.loadStoryPlays();
      } catch (e) {
        if (e !== "cancel") this.$message.error("删除失败");
      }
    },
    handlePlay(row) {
      this.$router.push(`/story-plays/${row.id}/play?skip_selector=1`);
    },
    async handleExport(row) {
      try {
        const { data } = await exportStoryPlay(row.id);
        const url = data.url;
        if (!url) {
          this.$message.error("导出失败：未生成下载链接");
          return;
        }
        const href = /^https?:\/\//i.test(url) ? url : `${window.location.origin}${url}`;
        const a = document.createElement("a");
        a.href = href;
        a.rel = "noopener";
        const filename = data.filename || `${(row.title || "story").replace(/[\s\\/:*?"<>|]+/g, "_")}_export.zip`;
        if (typeof a.download !== "undefined") {
          try { a.download = filename; } catch (_) { a.download = "export.zip"; }
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.$message.success("导出成功");
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "导出失败";
        this.$message.error(msg);
      }
    },
    async handleBundleDownload(row) {
      if (this.bundlePreparingId) {
        return;
      }
      this.bundlePreparingId = row.id;
      try {
        const { data } = await prepareStoryPlayBundleDownload(row.id);
        const rel = (data && data.url) || "";
        if (!rel) {
          this.$message.warning("服务器未返回下载地址");
          return;
        }
        const href = /^https?:\/\//i.test(rel) ? rel : `${window.location.origin}${rel}`;
        const safeTitle = (
          row.title ||
          row.id ||
          "story"
        )
          .replace(/[\s\\/:*?"<>|]+/g, "_")
          .trim()
          .slice(0, 80);
        const a = document.createElement("a");
        a.href = href;
        a.rel = "noopener";
        const name = data.filename || `storyplay_${row.id}.zip`;
        if (typeof a.download !== "undefined") {
          try {
            a.download = `${safeTitle}_${name}`;
          } catch (_) {
            a.download = name;
          }
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.$message.success((data && data.message) || "资源包已开始下载");
      } catch (e) {
        this.$message.error((e.response && e.response.data && e.response.data.message) || "打包下载失败");
      } finally {
        this.bundlePreparingId = null;
      }
    },
    handleImport() {
      this.importDialogVisible = true;
    },
    async handleImportUpload({ file }) {
      try {
        const text = await file.text();
        const payload = JSON.parse(text);
        const { data } = await importStoryPlay(payload);
        this.importDialogVisible = false;
        this.$message.success("导入成功");
        if (data && data.id) {
          this.$router.push(`/story-plays/${data.id}/edit`);
        } else {
          this.loadStoryPlays();
        }
      } catch (e) {
        if (e instanceof SyntaxError) {
          this.$message.error("JSON 格式错误");
        } else {
          this.$message.error("导入失败");
        }
      }
    },
    async handleCoverUpload({ file }) {
      try {
        const { data } = await uploadStoryImage(file);
        this.form.cover_image = data.url;
        this.$message.success("封面上传成功");
      } catch (e) {
        this.$message.error("封面上传失败");
      }
    }
  }
};
</script>

<style scoped>
.story-plays-page {
  padding: 20px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-header h2 {
  margin: 0;
}
.page-actions {
  display: flex;
  align-items: center;
}
.pagination-wrap {
  margin-top: 20px;
  text-align: right;
}
.cover-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
}
.cover-uploader:hover {
  border-color: #409eff;
}
.cover-upload-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}
.cover-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.composition-info {
  display: flex;
  justify-content: center;
  gap: 8px;
}
.comp-item {
  font-size: 13px;
  color: #606266;
}
.img-comp {
  color: #409eff;
}
.vid-comp {
  color: #67c23a;
}
.json-help {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}
.json-help p {
  margin: 8px 0 4px;
}
.json-help ul {
  margin: 0 0 10px 0;
  padding-left: 20px;
}
.json-help code {
  background-color: #f4f4f5;
  padding: 2px 4px;
  border-radius: 4px;
  color: #f56c6c;
  font-family: Consolas, Monaco, monospace;
}
.json-example {
  background-color: #282c34;
  color: #abb2bf;
  padding: 10px;
  border-radius: 6px;
  overflow-x: auto;
  font-family: Consolas, Monaco, monospace;
  margin: 0;
}
.json-example code {
  background-color: transparent;
  color: inherit;
  padding: 0;
}
</style>
